if (!customElements.get('cart-shipping-estimate-popup')) {
  const DESTINATION_STORAGE_KEY = 'mix-pallets-destination';
  const REQUEST_TIMEOUT_MS = 10000;
  const QUOTE_CACHE = new Map();
  const MAX_CACHE_ENTRIES = 12;

  class CartShippingEstimatePopup extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.fetchController = null;
      this.isOpen = false;
      this.hasSeenEstimate = false;
      this.i18n = this.decodeI18n(this.parseJson(this.dataset.i18n, {}));
      this.items = this.parseJson(this.querySelector('[data-shipping-items]')?.textContent, []);
      this.apiUrl = String(this.dataset.apiUrl || '').trim();
      this.cartSubtotal = Number.parseInt(this.dataset.cartSubtotal, 10) || 0;
      this.cartCurrency = String(this.dataset.cartCurrency || 'USD').toUpperCase();
      this.cartItemCount = Number.parseInt(this.dataset.cartItemCount, 10) || 0;
      this.overlay = this.querySelector('[data-estimate-overlay]');
      this.panel = this.querySelector('[data-estimate-panel]');
      this.openButton = this.querySelector('[data-estimate-open]');
      this.triggerLabel = this.querySelector('[data-estimate-trigger-label]');
      this.label = this.querySelector('[data-estimate-label]');
      this.summary = this.querySelector('[data-estimate-summary]');
      this.form = this.querySelector('[data-estimate-form]');
      this.countrySelect = this.querySelector('[data-estimate-country]');
      this.submitButton = this.querySelector('[data-estimate-submit]');
      this.output = this.querySelector('[data-estimate-output]');
      this.resultTitle = this.querySelector('[data-estimate-result-title]');
      this.skeleton = this.querySelector('[data-estimate-skeleton]');
      this.status = this.querySelector('[data-estimate-status]');
      this.rates = this.querySelector('[data-estimate-rates]');
      this.inertTargets = [];
      this.drawer = this.closest('cart-drawer');

      const { signal } = this.abortController;
      this.openButton?.addEventListener('click', () => this.open(), { signal });
      this.form?.addEventListener('submit', (event) => this.handleSubmit(event), { signal });
      this.overlay?.addEventListener('click', (event) => {
        if (event.target.closest('[data-estimate-close]')) this.close();
      }, { signal });
      this.countrySelect?.addEventListener('change', () => {
        this.fetchController?.abort();
        this.clearSummary();
        this.resetOutput();
      }, { signal });
      document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });

      this.restoreDestination();
    }

    disconnectedCallback() {
      this.fetchController?.abort();
      this.abortController?.abort();
      this.setDrawerInert(false);
      QUOTE_CACHE.clear();
    }

    parseJson(value, fallback) {
      if (!value) return fallback;
      try {
        return JSON.parse(value);
      } catch {
        return fallback;
      }
    }

    decodeHtmlEntities(value) {
      if (typeof value !== 'string' || !value.includes('&')) return value;
      const textarea = document.createElement('textarea');
      textarea.innerHTML = value;
      return textarea.value;
    }

    decodeI18n(i18n) {
      const decoded = {};
      for (const [key, value] of Object.entries(i18n || {})) {
        decoded[key] = typeof value === 'string' ? this.decodeHtmlEntities(value) : value;
      }
      return decoded;
    }

    restoreDestination() {
      if (!this.countrySelect) return;

      let storedCountry = '';
      try {
        storedCountry = String(
          this.parseJson(localStorage.getItem(DESTINATION_STORAGE_KEY), {})?.country || ''
        ).toUpperCase();
      } catch {
        storedCountry = '';
      }

      const customerCountry = String(this.dataset.customerCountry || '').trim().toLowerCase();
      const customerOption = Array.from(this.countrySelect.options).find((option) => {
        return String(option.dataset.countryName || option.textContent).trim().toLowerCase() === customerCountry;
      });
      const preferred = storedCountry
        || customerOption?.value
        || this.dataset.localizationCountry
        || '';

      if (Array.from(this.countrySelect.options).some((option) => option.value === preferred)) {
        this.countrySelect.value = preferred;
      }
    }

    open() {
      if (!this.overlay || !this.panel) return;
      this.isOpen = true;
      this.overlay.hidden = false;
      this.setDrawerInert(true);
      this.emit('on:cart-shipping-estimate:open', this.eventDetail());

      requestAnimationFrame(() => {
        this.overlay.classList.add('is-open');
        this.panel.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.overlay || !this.isOpen) return;
      const requestActive = Boolean(this.fetchController);
      this.fetchController?.abort();
      if (requestActive) this.resetOutput();
      this.isOpen = false;
      this.overlay.classList.remove('is-open');
      this.setDrawerInert(false);

      const finish = () => {
        if (!this.isOpen) this.overlay.hidden = true;
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
      } else {
        window.setTimeout(finish, 220);
      }
      this.openButton?.focus({ preventScroll: true });
    }

    setDrawerInert(inert) {
      const drawer = this.drawer;
      if (!drawer) return;
      if (inert) {
        const drawerSiblings = Array.from(drawer.children).filter((target) => !target.contains(this));
        const summarySiblings = Array.from(this.parentElement?.children || []).filter((target) => target !== this);
        this.inertTargets = [...drawerSiblings, ...summarySiblings];
        this.inertTargets.forEach((target) => { target.inert = true; });
        return;
      }
      this.inertTargets.forEach((target) => { target.inert = false; });
      this.inertTargets = [];
    }

    handleKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(this.panel.querySelectorAll(
        'button:not([disabled]), select:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )).filter((element) => !element.hidden);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async handleSubmit(event) {
      event.preventDefault();
      const country = this.countrySelect?.value;
      if (!country) {
        this.countrySelect?.reportValidity();
        return;
      }
      if (!this.apiUrl || !this.items.length) {
        this.showError(country, 0);
        return;
      }

      this.persistDestination(country);
      this.showLoading(country);
      const startedAt = performance.now();
      const cacheKey = `${this.cartSignature()}:${country}`;
      const cached = QUOTE_CACHE.get(cacheKey);
      this.emit('on:cart-shipping-estimate:submit', this.eventDetail({ country }));

      if (cached) {
        this.showRates(cached, country, 0);
        return;
      }

      this.fetchController?.abort();
      const controller = new AbortController();
      this.fetchController = controller;
      let timedOut = false;
      const timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch(this.apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: this.items,
            destination: {
              country,
              province: '',
              city: '',
              postal_code: '',
              address1: '',
              address2: '',
              name: '',
              company_name: '',
            },
          }),
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Estimate request failed: ${response.status}`);
        const data = await response.json();
        const scenarios = this.validScenarios(data?.scenarios);
        this.writeCache(cacheKey, scenarios);
        this.showRates(scenarios, country, Math.round(performance.now() - startedAt));
      } catch (error) {
        if (error.name === 'AbortError' && !timedOut) return;
        this.showError(country, Math.round(performance.now() - startedAt));
      } finally {
        window.clearTimeout(timeoutId);
        if (this.fetchController === controller) this.fetchController = null;
      }
    }

    validScenarios(value) {
      if (!Array.isArray(value)) return [];
      return value.filter((scenario) => {
        const shipping = Number.parseFloat(scenario?.shipping);
        const customs = Number.parseFloat(scenario?.customs);
        const total = Number.parseFloat(scenario?.total);
        return typeof scenario?.code === 'string' && scenario.code.trim().length > 0
          && typeof scenario?.name === 'string' && scenario.name.trim().length > 0
          && typeof scenario?.lead_time === 'string' && scenario.lead_time.trim().length > 0
          && typeof scenario?.currency === 'string' && scenario.currency.trim().length === 3
          && Number.isFinite(shipping)
          && Number.isFinite(customs)
          && Number.isFinite(total)
          && shipping >= 0
          && customs >= 0
          && total >= 0
          && Math.abs((shipping + customs) - total) < 0.011;
      });
    }

    showLoading(country) {
      this.clearSummary();
      this.resetOutput();
      this.output.hidden = false;
      this.output.setAttribute('aria-busy', 'true');
      this.resultTitle.textContent = this.resultHeading(country);
      this.skeleton.hidden = false;
      this.status.hidden = false;
      this.status.setAttribute('role', 'status');
      this.status.textContent = this.i18n.loading || 'Checking available rates...';
      this.submitButton.disabled = true;
    }

    showRates(scenarios, country, responseTime) {
      this.output.hidden = false;
      this.output.setAttribute('aria-busy', 'false');
      this.skeleton.hidden = true;
      this.submitButton.disabled = false;
      this.resultTitle.textContent = this.resultHeading(country);
      this.rates.replaceChildren();

      if (!scenarios.length) {
        this.status.hidden = false;
        this.status.setAttribute('role', 'status');
        this.status.textContent = this.i18n.empty || "We don't have an online rate for this destination. Contact us for a quote.";
        this.emit('on:cart-shipping-estimate:result', this.eventDetail({ country, responseTime, scenarioCount: 0 }));
        return;
      }

      this.status.hidden = true;
      scenarios.forEach((scenario) => this.rates.append(this.createRateCard(scenario)));
      const cheapest = scenarios.reduce((lowest, scenario) => {
        return Number.parseFloat(scenario.total) < Number.parseFloat(lowest.total) ? scenario : lowest;
      });
      this.updateSummary(country, cheapest);
      this.hasSeenEstimate = true;
      this.closest('cart-drawer')?.setAttribute('data-shipping-estimate-seen', 'true');
      this.emit('on:cart-shipping-estimate:result', this.eventDetail({
        country,
        responseTime,
        scenarioCount: scenarios.length,
      }));
    }

    showError(country, responseTime) {
      this.output.hidden = false;
      this.output.setAttribute('aria-busy', 'false');
      this.skeleton.hidden = true;
      this.rates.replaceChildren();
      this.submitButton.disabled = false;
      this.status.hidden = false;
      this.status.setAttribute('role', 'alert');
      this.status.textContent = this.i18n.error || "We couldn't load shipping rates. Please try again.";
      this.emit('on:cart-shipping-estimate:error', this.eventDetail({ country, responseTime }));
    }

    resetOutput() {
      if (!this.output) return;
      this.output.hidden = true;
      this.output.setAttribute('aria-busy', 'false');
      this.skeleton.hidden = true;
      this.status.hidden = true;
      this.status.textContent = '';
      this.rates.replaceChildren();
      this.submitButton.disabled = false;
    }

    createRateCard(scenario) {
      const currency = String(scenario.currency).toUpperCase();
      const card = document.createElement('article');
      card.className = 'cart-shipping-popup__rate';

      const top = document.createElement('div');
      top.className = 'cart-shipping-popup__rate-top';

      const title = document.createElement('h5');
      title.className = 'cart-shipping-popup__rate-name';
      title.textContent = scenario.name;

      const price = document.createElement('span');
      price.className = 'cart-shipping-popup__rate-price';
      price.textContent = this.formatCurrency(scenario.total, currency);
      top.append(title, price);

      const delivery = document.createElement('p');
      delivery.className = 'cart-shipping-popup__rate-delivery';
      delivery.textContent = scenario.lead_time
        ? (this.i18n.delivery || 'Estimated delivery: __LEAD_TIME__').replace('__LEAD_TIME__', scenario.lead_time)
        : (this.i18n.deliveryVaries || 'Estimated delivery varies');

      const note = document.createElement('p');
      note.className = 'cart-shipping-popup__rate-note';
      note.textContent = this.i18n.includesNote || 'Includes shipping and taxes';

      card.append(top, delivery, note);
      return card;
    }

    updateSummary(country, scenario) {
      const countryName = this.selectedCountryName();
      const price = this.formatCurrency(scenario.total, String(scenario.currency).toUpperCase());
      this.summary.textContent = (this.i18n.summary || 'Shipping to __COUNTRY__ from __PRICE__')
        .replace('__COUNTRY__', countryName || country)
        .replace('__PRICE__', price);
      this.summary.hidden = false;
      if (this.label) this.label.hidden = true;
      this.triggerLabel.textContent = this.i18n.change || 'Change';
    }

    clearSummary() {
      this.summary.textContent = '';
      this.summary.hidden = true;
      if (this.label) {
        this.label.hidden = false;
        this.label.textContent = this.i18n.label || 'Shipping';
      }
      this.triggerLabel.textContent = this.i18n.trigger || 'Estimate';
    }

    resultHeading(country) {
      return (this.i18n.resultHeading || 'Shipping to __COUNTRY__')
        .replace('__COUNTRY__', this.selectedCountryName() || country);
    }

    selectedCountryName() {
      return this.countrySelect?.selectedOptions?.[0]?.textContent?.trim() || '';
    }

    formatCurrency(value, currency) {
      const amount = Number.parseFloat(value);
      if (!Number.isFinite(amount)) return '';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
          currencyDisplay: currency === this.cartCurrency ? 'narrowSymbol' : 'code',
        }).format(amount);
      } catch {
        return `${amount.toFixed(2)} ${currency}`;
      }
    }

    persistDestination(country) {
      try {
        const current = this.parseJson(localStorage.getItem(DESTINATION_STORAGE_KEY), {});
        localStorage.setItem(DESTINATION_STORAGE_KEY, JSON.stringify({ ...current, country }));
      } catch {
        return;
      }
    }

    cartSignature() {
      return this.items
        .map((item) => `${item.variant_id}:${item.qty}`)
        .sort()
        .concat(`subtotal:${this.cartSubtotal}`)
        .join(',');
    }

    writeCache(key, value) {
      QUOTE_CACHE.set(key, value);
      if (QUOTE_CACHE.size > MAX_CACHE_ENTRIES) {
        QUOTE_CACHE.delete(QUOTE_CACHE.keys().next().value);
      }
    }

    eventDetail(extra = {}) {
      const minimumMet = this.closest('cart-drawer')
        ?.querySelector('[data-minimum-met]')
        ?.dataset.minimumMet !== 'false';
      return {
        country: this.countrySelect?.value || '',
        cartValue: this.cartSubtotal,
        itemCount: this.cartItemCount,
        minimumMet,
        estimateSeen: this.hasSeenEstimate,
        ...extra,
      };
    }

    emit(name, detail) {
      this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
    }
  }

  customElements.define('cart-shipping-estimate-popup', CartShippingEstimatePopup);
}
